import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, History, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function fmtWhen(id) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})Z(?:_(\d+))?$/.exec(id || "");
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}:${m[6]} UTC${m[7] ? ` #${m[7]}` : ""}` : id;
}

export default function SnapshotPanel({ db, onChange }) {
  const [items, setItems] = useState(null);
  const [extra, setExtra] = useState(0);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const [restore, setRestore] = useState(null);
  const [asName, setAsName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/databases/${db}/snapshots`);
      setItems(data.items); setExtra(data.bytes_exclusive_total);
    } catch (e) { toast.error(errMsg(e)); }
  }, [db]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/databases/${db}/snapshots`, { label });
      toast.success(`Snapshot ${fmtWhen(data.id)} criado (${data.kv_ssts + data.idx_ssts} SSTables, custo em disco ~0)`);
      setLabel(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    const id = del; setDel(null);
    try { await api.delete(`/databases/${db}/snapshots/${id}`); toast.success("Snapshot apagado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doRestore = async () => {
    const id = restore; const target = asName.trim(); setRestore(null); setBusy(true);
    try {
      const { data } = await api.post(`/databases/${db}/snapshots/${id}/restore`, target ? { as: target } : {});
      toast.success(target ? `Restaurado como banco novo "${target}"` : `Banco "${db}" restaurado para ${fmtWhen(id)}`);
      setAsName(""); load(); onChange?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="snapshot-panel">
      <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 space-y-4">
        <div className="flex items-center gap-2 text-white font-display"><Camera className="h-4 w-4 text-orange-500" /> Snapshots (backup instantâneo)</div>
        <p className="text-sm text-zinc-400">
          Um snapshot congela o banco neste instante usando <b>hard links</b> das SSTables (imutáveis): leva milissegundos e ocupa ~0 bytes até que a
          compactação substitua arquivos. Restaurar é igualmente rápido e faz um snapshot automático do estado atual antes de substituir.
          Para backup automático diário e para <b>levar este banco a outro servidor</b>, use a página <Link to="/backup" className="text-orange-400 hover:text-orange-300 underline underline-offset-2" data-testid="snapshot-goto-backup">Backup</Link>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="snap-label" className="text-zinc-300">Rótulo (opcional)</Label>
            <Input id="snap-label" data-testid="snapshot-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex.: antes da carga de junho" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
          </div>
          <Button onClick={create} disabled={busy} data-testid="snapshot-create-btn" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile"><Camera className="h-4 w-4 mr-1" /> Criar snapshot</Button>
          <Button variant="outline" onClick={load} data-testid="snapshot-refresh-btn" className="rounded-sm border-zinc-700"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="rounded-sm border border-zinc-800 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 text-sm">
          <span className="text-zinc-300 flex items-center gap-2"><History className="h-4 w-4" /> {items ? `${items.length} snapshot(s)` : "carregando…"}</span>
          <span className="text-zinc-500" data-testid="snapshot-extra-bytes">espaço extra retido: {fmtBytes(extra)}</span>
        </div>
        {items && items.length === 0 && <div className="px-4 py-8 text-center text-zinc-500 text-sm" data-testid="snapshot-empty">Nenhum snapshot ainda.</div>}
        {items?.map((s) => (
          <div key={s.id} data-testid={`snapshot-row-${s.id}`} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 border-b border-zinc-800/60 last:border-0 hover:bg-zinc-900/40 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm text-white">{fmtWhen(s.id)} {s.label && <span className="ml-2 text-zinc-400 font-sans">· {s.label}</span>}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{s.kv_ssts} SST dados · {s.idx_ssts} SST índice · {s.collections} coleções · lógico {fmtBytes(s.bytes_total)} · retido só aqui {fmtBytes(s.bytes_exclusive)}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setRestore(s.id)} disabled={busy} data-testid={`snapshot-restore-${s.id}`} className="rounded-sm border-zinc-700"><RotateCcw className="h-4 w-4 mr-1" /> Restaurar</Button>
              <Button size="sm" variant="outline" onClick={() => setDel(s.id)} data-testid={`snapshot-delete-${s.id}`} className="rounded-sm border-zinc-700 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Apagar snapshot?" description={`O snapshot ${fmtWhen(del)} será removido. Libera apenas o espaço que só ele retém.`} onConfirm={doDelete} testId="snapshot-delete-dialog" />

      <AlertDialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="snapshot-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Restaurar snapshot {fmtWhen(restore)}</AlertDialogTitle>
            <AlertDialogDescription>
              Deixe o campo vazio para voltar o banco <b>{db}</b> a este ponto (um snapshot "pre-restore" do estado atual é criado antes). Ou informe um nome para restaurar como um <b>banco novo</b> e conferir sem mexer no atual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="restore-as" className="text-zinc-300">Restaurar como (opcional)</Label>
            <Input id="restore-as" data-testid="snapshot-restore-as-input" value={asName} onChange={(e) => setAsName(e.target.value)} placeholder={`${db}_copia`} className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="snapshot-restore-cancel" className="rounded-sm border-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} data-testid="snapshot-restore-confirm" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile">Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
